<script setup lang="ts">
    import ArrowBackIcon from "@/assets/arrow_back_dark.svg";
    import ChainIcon from "@/assets/chain_dark.svg";
    import KontrolleratIcon from "@/assets/kontrollerat.svg";
    import LockRedIcon from "@/assets/lock_red.svg";
    import LockUserIcon from "@/assets/lock_user_red.svg";
    import MakuleradIcon from "@/assets/makulerad.svg";
    import Narkotika2Icon from "@/assets/narkotika_2.svg";
    import Narkotika3Icon from "@/assets/narkotika_3.svg";
    import Narkotika45Icon from "@/assets/narkotika_4_5.svg";
    import KTag from "@/components/KTag.vue";
    import KTabContainer from "@/components/Tab/KTabContainer.vue";
    import { checkNllLocks, NllLocksDisplay, formatProductTitle } from "@/tabs/Receptforskrivning/helpers";
    import ReceptTabForskrivning from "@/tabs/Receptforskrivning/Start/List/ReceptTabForskrivning.vue";
    import ReceptTabHandelser from "@/tabs/Receptforskrivning/Start/List/ReceptTabHandelser.vue";
    import ReceptTabUttag from "@/tabs/Receptforskrivning/Start/List/ReceptTabUttag.vue";
    import { Forskrivningsstatus, ReceptforskrivningDisplay } from "@/tabs/Receptforskrivning/types";
    import { computed } from "vue";

    const props = defineProps<{
        data: ReceptforskrivningDisplay;
    }>();

    const locks = computed<NllLocksDisplay | null>(() => {
        return checkNllLocks({ sparrar: props.data?.sparrar });
    });
</script>

<template>
    <div class="receptforskrivning-list-expanded">
        <p class="expanded-title">
            {{ formatProductTitle({ artikelInfo: data.lakemedelInfo }) }}
        </p>
        <div class="tags">
            <KTag v-if="locks?.integritet">
                <template #icon>
                    <img :src="LockUserIcon" alt="Spärrad förskrivning" />
                </template>
                <template #default>
                    <span>Spärrad förskrivning</span>
                </template>
            </KTag>
            <KTag v-if="locks?.behandlingsorsak">
                <template #icon>
                    <img :src="LockRedIcon" alt="Behandlingsorsak spärrad för patient" />
                </template>
                <template #default>
                    <span>Behandlingsorsak spärrad för patient</span>
                </template>
            </KTag>

            <KTag v-if="locks?.sekretessVardnadshavare">
                <template #icon>
                    <img :src="LockRedIcon" alt="Spärrad för vårdnadshavare" />
                </template>
                <template #default>
                    <span>Spärrad för vårdnadshavare</span>
                </template>
            </KTag>

            <KTag v-if="locks?.behandlingsorsakIntegritet">
                <template #icon>
                    <img :src="LockUserIcon" alt="Behandlingsorsak spärrad av patient" />
                </template>
                <template #default>
                    <span>Behandlingsorsak spärrad av patient</span>
                </template>
            </KTag>

            <KTag v-if="data.ikoner?.parallell">
                <template #icon>
                    <img :src="ArrowBackIcon" alt="Parallell förskrivning" />
                </template>
                <template #default>
                    <span>Parallell förskrivning</span>
                </template>
            </KTag>

            <KTag v-if="data.ikoner?.narkotikaNiva && data.ikoner?.narkotikaNiva === 2">
                <template #icon>
                    <img :src="Narkotika2Icon" alt="Narkotika II" />
                </template>
                <template #default>
                    <span>Narkotika II</span>
                </template>
            </KTag>

            <KTag v-if="data.ikoner?.narkotikaNiva && data.ikoner?.narkotikaNiva === 3">
                <template #icon>
                    <img :src="Narkotika3Icon" alt="Narkotika III" />
                </template>
                <template #default>
                    <span>Narkotika III</span>
                </template>
            </KTag>

            <KTag v-if="data.ikoner?.narkotikaNiva && data.ikoner?.narkotikaNiva >= 4">
                <template #icon>
                    <img :src="Narkotika45Icon" alt="Narkotika IV V" />
                </template>
                <template #default>
                    <span>Narkotika IV V</span>
                </template>
            </KTag>

            <KTag v-if="data.ikoner?.kedja">
                <template #icon>
                    <img :src="ChainIcon" alt="Förskrivningskedja" />
                </template>
                <template #default>
                    <span>Förskrivningskedja</span>
                </template>
            </KTag>

            <KTag v-if="data.ikoner?.kontrollerat">
                <template #icon>
                    <img :src="KontrolleratIcon" alt="Särskilt läkemedel" />
                </template>
                <template #default>
                    <span>Särskilt läkemedel</span>
                </template>
            </KTag>

            <KTag v-if="data.forskrivningsstatus === Forskrivningsstatus.Makulerad">
                <template #icon>
                    <img :src="MakuleradIcon" alt="Särskilt läkemedel" />
                </template>
                <template #default>
                    <span>Makulerad</span>
                </template>
            </KTag>
        </div>
        <KTabContainer
            variant="simple"
            :tabs="[
                {
                    data: {
                        tabHeader: 'Förskrivning',
                        ...data,
                    },
                    bodyTemplate: ReceptTabForskrivning,
                },
                {
                    data: {
                        tabHeader: 'Händelser',
                        ...data,
                    },
                    bodyTemplate: ReceptTabHandelser,
                },
                {
                    data: {
                        tabHeader: 'Uttag',
                        ...data,
                    },
                    bodyTemplate: ReceptTabUttag,
                },
            ]"
        />
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";

    .receptforskrivning-list-expanded {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        .expanded-title {
            color: $color-active-dark;
            font-family: Roboto Medium, sans-serif;
        }

        .tags {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;

            .k-tag {
                background-color: $color-light;
            }
        }
    }
</style>
